import React, { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery } from 'react-query'
import { AlertCircle, Search } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'
import { taskService } from '@/services/taskService'
import { projectService } from '@/services/projectService'
import { sprintService } from '@/services/sprintService'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import KanbanBoard from '@/components/ProjectCentral/KanbanBoard'
import ProjectLayout from '@/components/ProjectCentral/ProjectLayout'
import TaskFilters from '@/components/ProjectCentral/TaskFilters'
import { Skeleton } from '@/components/ui/skeleton'
import api from '@/services/api'

const ProjectBoard = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const [search, setSearch] = useState('')
  const [filters, setFilters] = useState({})
  const [sprintChoice, setSprintChoice] = useState(null) // null = default: the active sprint if there is one

  const { data: sprintData } = useQuery(['projects', id, 'sprints'], () => sprintService.getSprints(id))
  const openSprints = sprintData?.data?.sprints || []
  const activeSprint = openSprints.find(s => s.state === 'active')
  const sprintFilter = sprintChoice ?? (activeSprint ? 'active' : 'all')

  // Build query params
  const queryParams = {
    view: 'board',
    ...filters,
    ...(search ? { search } : {}),
    ...(sprintFilter !== 'all' ? { sprint_id: sprintFilter } : {}),
  }

  const { data: boardData, isLoading, isError, error, refetch } = useQuery(
    ['projects', id, 'tasks', queryParams],
    () => taskService.getTasks(id, queryParams),
    { refetchOnWindowFocus: false, keepPreviousData: true, retry: (count, err) => err?.response?.status !== 403 && count < 3 }
  )

  const { data: projectData } = useQuery(
    ['projects', id],
    () => projectService.getProject(id)
  )

  const { data: statusData } = useQuery(
    ['projects', id, 'statuses'],
    () => projectService.getStatuses(id)
  )

  const { data: usersData } = useQuery(
    'team-members',
    async () => { const r = await api.get('/users/team-members'); return r.data }
  )

  const project = projectData?.data?.project
  const board = boardData?.data?.board || []
  const statuses = statusData?.data?.statuses || []
  const users = usersData?.data?.users || []

  const getUserName = (user) =>
    user.employee ? `${user.employee.first_name} ${user.employee.last_name}` : user.email

  const handleTaskClick = (task) => {
    navigate(`/projects/${id}/tasks/${task.id}`)
  }

  const handleAssigneeToggle = (userId) => {
    const val = String(userId)
    setFilters(prev => {
      const current = prev.assignee_id ? prev.assignee_id.split(',') : []
      const idx = current.indexOf(val)
      if (idx >= 0) {
        current.splice(idx, 1)
      } else {
        current.push(val)
      }
      return { ...prev, assignee_id: current.length > 0 ? current.join(',') : undefined }
    })
  }

  return (
    <ProjectLayout>
      <div className="flex flex-col gap-4 h-full">
        {/* Filter bar */}
        <div className="flex items-center gap-3 flex-wrap">
          {/* Sprint (Jira's Scrum board shows the active sprint) */}
          {openSprints.length > 0 && (
            <Select value={sprintFilter} onValueChange={setSprintChoice}>
              <SelectTrigger className="h-9 w-[200px]" aria-label="Sprint">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {activeSprint && <SelectItem value="active">{activeSprint.name} (active)</SelectItem>}
                {openSprints.filter(s => s.state === 'planned').map(s => <SelectItem key={s.id} value={String(s.id)}>{s.name}</SelectItem>)}
                <SelectItem value="none">Backlog (no sprint)</SelectItem>
                <SelectItem value="all">All tasks</SelectItem>
              </SelectContent>
            </Select>
          )}

          {/* Search */}
          <div className="relative w-48">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search board..."
              className="pl-8 h-9"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          {/* Quick assignee avatars */}
          {users.length > 0 && (
            <div className="flex items-center -space-x-2">
              {users.map((user) => {
                const name = getUserName(user)
                const isSelected = filters.assignee_id?.split(',').includes(String(user.id))
                return (
                  <Tooltip key={user.id}>
                    <TooltipTrigger asChild>
                      <button
                        onClick={() => handleAssigneeToggle(user.id)}
                        className={cn(
                          'rounded-full transition-all relative hover:z-10',
                          isSelected
                            ? 'ring-2 ring-primary ring-offset-2 z-10'
                            : 'opacity-80 hover:opacity-100'
                        )}
                      >
                        <Avatar className="h-8 w-8 border-2 border-background">
                          <AvatarFallback className="text-[10px] bg-primary/10 text-primary">
                            {name.charAt(0).toUpperCase()}
                          </AvatarFallback>
                        </Avatar>
                      </button>
                    </TooltipTrigger>
                    <TooltipContent side="bottom" className="text-xs">
                      {name}
                    </TooltipContent>
                  </Tooltip>
                )
              })}
            </div>
          )}

          {/* Filters */}
          <TaskFilters
            filters={filters}
            projectId={id}
            onChange={setFilters}
            statuses={statuses}
            users={users}
            hideLabel
            hideStatus
          />
        </div>

        {/* Board */}
        {isError ? (
          <div className="flex flex-col items-center justify-center h-64 text-center">
            <AlertCircle className="h-8 w-8 text-destructive mb-2" />
            <p className="font-medium">{error?.response?.status === 403 ? 'Access Denied' : 'Failed to load board'}</p>
            <p className="text-muted-foreground text-sm mt-1">{error?.response?.status === 403 ? 'You do not have access to this project.' : 'Something went wrong. Please try again.'}</p>
            {error?.response?.status === 403
              ? <Button variant="outline" size="sm" className="mt-3" onClick={() => navigate('/projects')}>Back to Projects</Button>
              : <Button variant="outline" size="sm" className="mt-3" onClick={() => refetch()}>Retry</Button>
            }
          </div>
        ) : isLoading ? (
          <div className="flex gap-4 overflow-x-auto pb-4">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="flex flex-col w-72 shrink-0 gap-2">
                <Skeleton className="h-6 w-28" />
                <div className="space-y-2 bg-muted/30 rounded-lg p-2 min-h-[80px]">
                  {Array.from({ length: i === 0 ? 3 : i === 1 ? 2 : 1 }).map((_, j) => (
                    <Skeleton key={j} className="h-20 w-full rounded-md" />
                  ))}
                </div>
              </div>
            ))}
          </div>
        ) : board.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64 text-center">
            <p className="text-muted-foreground">No statuses configured.</p>
            <Button
              variant="link"
              onClick={() => navigate(`/projects/${id}/settings`)}
            >
              Go to Settings to add statuses
            </Button>
          </div>
        ) : (
          <KanbanBoard
            projectId={id}
            board={board}
            onTaskClick={handleTaskClick}
            projectKey={project?.key}
          />
        )}
      </div>
    </ProjectLayout>
  )
}

export default ProjectBoard
